import { createSelector } from 'reselect'
import { DateTime } from 'luxon'

import { MembershipScopes } from '@/reducers/auth/userReducer'
import {
  selectHeardProductPlanInterval,
  selectHeardProductSubscription,
  selectIsBasicPlan,
} from '@/reducers/subscription.slice'
import {
  getCurrentUser,
  membershipIsIn,
  selectPrimaryMembership,
} from '@/selectors/user.selectors'
import { STRIPE_PRICE_MONTH_INTERVAL } from '@/actions/settings/billingActions'
import { selectIsPayrollActive } from '@/features/payroll/payroll.selectors'
import { hasTaxfyleJobOrExtensionInProgress } from '@/features/taxes/annual-taxes/annualTaxFilings.selector'
import { selectBankingApplicationStatus } from '@/features/banking/banking.selectors'
import { BankingApplicationStatus } from '@/features/banking/banking.types'

/**
 * A user is eligble for the cancellation discount if they:
 * 1. are not on a basic plan
 * 2. have not received the discount before
 * 3. do not have any other current discounts
 * 4. are not currently on a trial
 * 5. were not on a trial within the last year
 */
export const isEligibleForDiscount = createSelector(
  getCurrentUser,
  selectIsBasicPlan,
  selectHeardProductSubscription,
  selectPrimaryMembership,
  (_: unknown, discountFlagOn: boolean) => discountFlagOn,
  (user, isBasicPlan, sub, membership, discountFlagOn) =>
    discountFlagOn &&
    !isBasicPlan &&
    user?.cancellationDiscountApplied !== true &&
    sub?.discounts.length === 0 &&
    !membershipIsIn(MembershipScopes.trial, membership) &&
    (!membership?.trialStartDate ||
      DateTime.fromISO(membership.trialStartDate) <
        DateTime.now().minus({ years: 1 }))
)

/**
 * Returns true if the user has an annual commitment on their primary membership
 * NOTE: Do not use for active commitment checks, use selectUserHasActiveAnnualCommitment instead
 */
export const selectHasAnnualCommitment = createSelector(
  selectPrimaryMembership,
  (primaryMembership) => Boolean(primaryMembership?.hasAnnualCommitment)
)

/**
 * Returns true if the user has an active annual commitment
 * Mirrors plan change's `inCommittedTerm`: no expiry while billed monthly, none during a trial.
 */
export const selectUserHasActiveAnnualCommitment = createSelector(
  selectPrimaryMembership,
  selectHeardProductPlanInterval,
  (primaryMembership, planInterval) =>
    Boolean(primaryMembership?.hasAnnualCommitment) &&
    planInterval === STRIPE_PRICE_MONTH_INTERVAL &&
    !membershipIsIn(MembershipScopes.trial, primaryMembership)
)

/**
 * Returns true if the user has a Heard Banking application that must be
 * resolved before they can self-serve cancel. Unit support handles application
 * cancellation / account closure separately from Heard cancellation.
 */
export const selectHasBlockingBankingProfile = createSelector(
  selectBankingApplicationStatus,
  (status) =>
    status === BankingApplicationStatus.Approved ||
    status === BankingApplicationStatus.PendingReview
)

export const selectCanSelfServeCancel = createSelector(
  selectUserHasActiveAnnualCommitment,
  selectIsPayrollActive,
  hasTaxfyleJobOrExtensionInProgress,
  selectHasBlockingBankingProfile,
  (
    hasActiveAnnualCommitment,
    isPayrollActive,
    hasTaxfyleJobInProgress,
    hasBlockingBanking
  ) =>
    !hasActiveAnnualCommitment &&
    !isPayrollActive &&
    !hasTaxfyleJobInProgress &&
    !hasBlockingBanking
)
